import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useStore from '../store/useStore';

const API = 'https://zengrid.space';
function tok() { return localStorage.getItem('mv_token') || ''; }
function hdr() { return { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' }; }

// ── Date helpers ──────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}
function daysLeft(s) {
  return Math.max(0, Math.ceil((new Date(s) - Date.now()) / 86400000));
}

// ── Electron-standard design tokens ──────────────────────────────────────────
const C = {
  bg:       '#0d1117',
  surface:  '#161b22',
  surface2: '#1c2128',
  border:   '#30363d',
  border2:  '#21262d',
  text:     '#e6edf3',
  text2:    '#c9d1d9',
  text3:    '#8b949e',
  text4:    '#6e7681',
  accent:   '#1f6feb',
  green:    '#2ea043',
  yellow:   '#d29922',
  red:      '#f85149',
};

function Btn({ variant = 'ghost', onClick, disabled, children, style = {} }) {
  const [hover, setHover] = useState(false);
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    height: 36, padding: '0 16px', borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
    transition: 'background .12s, border-color .12s, opacity .12s',
    userSelect: 'none', flexShrink: 0, opacity: disabled ? 0.45 : 1, border: '1px solid transparent',
  };
  const variants = {
    ghost:   { background: hover ? C.surface2 : C.surface, color: C.text2, border: `1px solid ${C.border}` },
    primary: { background: hover ? '#238636' : C.green,   color: '#fff',   border: `1px solid ${hover ? '#2ea043' : C.green}` },
    danger:  { background: hover ? 'rgba(248,81,73,.15)' : 'transparent', color: C.red, border: `1px solid ${C.red}` },
    accent:  { background: hover ? 'rgba(31,111,235,.22)' : 'rgba(31,111,235,.12)', color: '#58a6ff', border: `1px solid rgba(31,111,235,.4)` },
    success: { background: hover ? 'rgba(46,160,67,.22)' : 'rgba(46,160,67,.12)', color: '#3fb950', border: `1px solid rgba(46,160,67,.4)` },
    flat:    { background: 'transparent', color: C.text3, border: '1px solid transparent' },
  };
  return (
    <button
      style={{ ...base, ...variants[variant], ...style }}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      {children}
    </button>
  );
}

function Tag({ status }) {
  const map = {
    valid:          { label: '✓ Valid',          color: C.green,  bg: 'rgba(46,160,67,.14)'  },
    invalid_mailbox:{ label: '✗ Rejected',       color: C.red,    bg: 'rgba(248,81,73,.14)'  },
    no_mx:          { label: '✗ No MX',           color: C.red,    bg: 'rgba(248,81,73,.14)'  },
    domain_not_found:{ label: '✗ No Domain',     color: C.red,    bg: 'rgba(248,81,73,.14)'  },
    invalid_format: { label: '✗ Bad Format',      color: C.red,    bg: 'rgba(248,81,73,.14)'  },
    catchall:       { label: '⚠ Catch-all',       color: C.yellow, bg: 'rgba(210,153,34,.14)' },
    smtp_unreachable:{ label: '? Unreachable',    color: C.yellow, bg: 'rgba(210,153,34,.14)' },
  };
  const t = map[status] || { label: status, color: C.text3, bg: C.surface2 };
  return (
    <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: t.color, background: t.bg, whiteSpace: 'nowrap' }}>
      {t.label}
    </span>
  );
}

// ── Animated counter ──────────────────────────────────────────────────────────
function AnimCounter({ target, running }) {
  const [display, setDisplay] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!running) { setDisplay(target); return; }

    // Smooth animation toward target, plus gentle +1 ticks while waiting for next real update
    const tick = () => {
      setDisplay(prev => {
        if (prev < target) return target;
        // Add gentle mock increment (1–3 per 120ms) so counter never stands still
        return prev + Math.floor(Math.random() * 3) + 1;
      });
    };
    frameRef.current = setInterval(tick, 120);
    return () => clearInterval(frameRef.current);
  }, [running, target]);

  // Sync immediately when real value jumps ahead
  useEffect(() => {
    setDisplay(prev => Math.max(prev, target));
  }, [target]);

  return <span>{display.toLocaleString()}</span>;
}

// ── File/attachment helpers ───────────────────────────────────────────────────
function fileCategory(contentType = '', filename = '') {
  const ct = contentType.toLowerCase();
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (ct.startsWith('image/') || ['jpg','jpeg','png','gif','bmp','webp','svg','ico','tiff'].includes(ext)) return 'image';
  if (
    ct.includes('pdf') || ext === 'pdf' ||
    ct.includes('msword') || ct.includes('officedocument.word') || ['doc','docx','odt'].includes(ext) ||
    ct.includes('excel') || ct.includes('spreadsheet') || ['xls','xlsx','csv','ods'].includes(ext) ||
    ct.includes('powerpoint') || ct.includes('presentation') || ['ppt','pptx','odp'].includes(ext) ||
    ct.includes('text/plain') || ['txt','md','rtf'].includes(ext)
  ) return 'doc';
  return 'file';
}
function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
function fileIcon(contentType = '', filename = '') {
  const cat = fileCategory(contentType, filename);
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (cat === 'image') return '🖼️';
  if (['pdf'].includes(ext)) return '📄';
  if (['xls','xlsx','csv','ods'].includes(ext) || contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
  if (['ppt','pptx','odp'].includes(ext) || contentType.includes('powerpoint')) return '📊';
  if (cat === 'doc') return '📝';
  if (contentType.includes('zip') || contentType.includes('compress') || ['zip','rar','7z','tar','gz'].includes(ext)) return '🗜️';
  return '📎';
}

// ── Verification modal ────────────────────────────────────────────────────────
function VerifyModal({ emails, sourceResultId, sourceAccountEmail, isSA, onClose, onSaved }) {
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    run();
    return () => { cancelled.current = true; };
  }, []);

  const BATCH = 30;
  async function run() {
    const all = emails.map(e => e.email);
    const out = [];
    for (let i = 0; i < all.length; i += BATCH) {
      if (cancelled.current) return;
      const slice = all.slice(i, i + BATCH);
      try {
        const r = await fetch(`${API}/api/extractor/verify`, {
          method: 'POST', headers: hdr(), body: JSON.stringify({ emails: slice }),
        });
        if (r.ok) out.push(...(await r.json()).results);
      } catch {}
      setProgress(Math.min(100, Math.round((i + BATCH) / all.length * 100)));
      setResults([...out]);
    }
    const valid = out.filter(r => r.status === 'valid').length;
    const catchall = out.filter(r => r.status === 'catchall').length;
    const unreachable = out.filter(r => r.status === 'smtp_unreachable').length;
    const rejected = out.filter(r => ['invalid_mailbox','no_mx','domain_not_found','invalid_format'].includes(r.status)).length;
    setStats({ valid, catchall, unreachable, rejected, total: out.length });
    setDone(true);
  }

  const saveResults = async () => {
    if (saving || saved) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/extractor/verifications`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({
          results,
          source_result_id: sourceResultId,
          source_account_email: sourceAccountEmail,
        }),
      });
      if (r.ok) { setSaved(true); onSaved && onSaved(); }
      else { setSaveErr('Save failed — try again'); setTimeout(() => setSaveErr(''), 3000); }
    } catch { setSaveErr('Save failed — try again'); setTimeout(() => setSaveErr(''), 3000); }
    finally { setSaving(false); }
  };

  const downloadCsv = () => {
    const rows = [['Email', 'Status', 'Reason']];
    results.forEach(r => rows.push([r.email, r.status, r.reason || '']));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `verification_${Date.now()}.csv`; a.click();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,.6)' }}>
        {/* Title bar */}
        <div style={{ padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border2}`, flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>Email Verification</span>
          <Btn variant="flat" onClick={onClose} style={{ height: 32, padding: '0 10px', fontSize: 18, color: C.text3 }}>✕</Btn>
        </div>

        {/* Progress section */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border2}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
            <span style={{ color: C.text3 }}>{done ? 'Verification complete' : `Checking ${results.length} / ${emails.length}…`}</span>
            <span style={{ color: C.text2, fontWeight: 600 }}>{progress}%</span>
          </div>
          {/* Progress bar */}
          <div style={{ height: 8, background: C.surface2, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: done ? C.green : C.accent, borderRadius: 4, transition: 'width .3s ease' }} />
          </div>

          {/* Stats chips */}
          {done && stats && (
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              {[
                { label: 'Valid',        count: stats.valid,       color: C.green,  bg: 'rgba(46,160,67,.1)'  },
                { label: 'Catch-all',    count: stats.catchall,    color: C.yellow, bg: 'rgba(210,153,34,.1)' },
                { label: 'Unreachable',  count: stats.unreachable, color: C.yellow, bg: 'rgba(210,153,34,.1)' },
                { label: 'Rejected',     count: stats.rejected,    color: C.red,    bg: 'rgba(248,81,73,.1)'  },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, minWidth: 100, textAlign: 'center', background: s.bg, border: `1px solid ${s.color}40`, borderRadius: 8, padding: '10px 8px' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.count}</div>
                  <div style={{ fontSize: 11, color: C.text3, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Results table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {results.length === 0 && !done && (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: C.text3, fontSize: 14 }}>⏳ Starting verification…</div>
          )}
          {results.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                  {['#', 'Email Address', 'Status', 'Detail'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.text3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={r.email} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.015)' }}>
                    <td style={{ padding: '9px 14px', color: C.text4, fontSize: 12, width: 40 }}>{i + 1}</td>
                    <td style={{ padding: '9px 14px', color: C.text, fontFamily: 'monospace', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</td>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}><Tag status={r.status} /></td>
                    <td style={{ padding: '9px 14px', color: C.text3, fontSize: 12 }}>{r.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {done && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border2}`, display: 'flex', gap: 10, flexShrink: 0, justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: C.text3 }}>
              {saved ? <span style={{ color: C.green }}>✓ Saved to server (30d)</span> : saveErr ? <span style={{ color: '#d13438' }}>{saveErr}</span> : 'Save results to access later in Verified Emails'}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" onClick={downloadCsv}>↓ Download CSV</Btn>
              {!saved && <Btn variant="success" onClick={saveResults} disabled={saving}>{saving ? 'Saving…' : '💾 Save Results'}</Btn>}
              <Btn variant="ghost" onClick={onClose}>Close</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Email list detail modal ───────────────────────────────────────────────────
function EmailDetailModal({ result, isSA, onClose, onDelete, onVerified }) {
  const [copied, setCopied] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [modalTab, setModalTab] = useState('emails');
  const emails    = result.emails || [];
  const keywords  = result.keywords || [];
  const allAtts   = result.attachments || [];
  const docs      = allAtts.filter(a => fileCategory(a.content_type, a.name) === 'doc');
  const images    = allAtts.filter(a => fileCategory(a.content_type, a.name) === 'image');
  const otherFiles= allAtts.filter(a => fileCategory(a.content_type, a.name) === 'file');

  const downloadAtt = async (att) => {
    if (!result.account_id) { alert('Account ID not available — re-run the extraction to enable downloads.'); return; }
    try {
      const r = await fetch(
        `${API}/api/extractor/attachment/${result.account_id}?message_id=${encodeURIComponent(att.message_id)}&attachment_id=${encodeURIComponent(att.attachment_id)}&filename=${encodeURIComponent(att.name)}`,
        { headers: hdr() }
      );
      if (!r.ok) { alert('Download failed — account may need reconnecting.'); return; }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = att.name; a.click();
      URL.revokeObjectURL(a.href);
    } catch { alert('Download error'); }
  };

  const modalTabs = [
    { key: 'emails',   label: 'Emails',   count: emails.length    },
    ...(allAtts.length > 0 ? [
      { key: 'files',    label: 'Files',    count: otherFiles.length },
      { key: 'docs',     label: 'Docs',     count: docs.length       },
      { key: 'images',   label: 'Images',   count: images.length     },
    ] : []),
    ...(keywords.length > 0 ? [{ key: 'keywords', label: 'Keywords', count: keywords.length }] : []),
  ];

  const copyAll = () => {
    navigator.clipboard.writeText(emails.map(e => e.email).join('\n'));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  const downloadCsv = () => {
    const rows = [['Email', 'Name', 'Count']];
    emails.forEach(e => rows.push([e.email, e.name || '', e.count]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `emails_${result.account_email}_${result.created_at.split('T')[0]}.csv`;
    a.click();
  };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, width: '100%', maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,.5)' }}>
          {/* Title bar */}
          <div style={{ padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border2}`, flexShrink: 0 }}>
            <div>
              <span style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{emails.length.toLocaleString()} Emails</span>
              <span style={{ color: C.text3, fontSize: 12, marginLeft: 10 }}>from {result.account_email}</span>
              {isSA && <span style={{ marginLeft: 8, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 11, color: C.text2 }}>by {result.owner_username}</span>}
            </div>
            <Btn variant="flat" onClick={onClose} style={{ height: 32, padding: '0 10px', fontSize: 18, color: C.text3 }}>✕</Btn>
          </div>

          {/* Tab bar + actions */}
          <div style={{ display: 'flex', alignItems: 'center', background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '0 12px', flexShrink: 0 }}>
            {modalTabs.map(t => (
              <button key={t.key} onClick={() => setModalTab(t.key)} style={{
                height: 40, padding: '0 14px', border: 'none', cursor: 'pointer',
                background: 'transparent', fontFamily: 'inherit', fontSize: 13,
                color: modalTab === t.key ? C.text : C.text3,
                fontWeight: modalTab === t.key ? 600 : 400,
                borderBottom: modalTab === t.key ? `2px solid ${C.accent}` : '2px solid transparent',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                {t.label}
                {t.count > 0 && <span style={{ background: modalTab === t.key ? 'rgba(31,111,235,.2)' : C.surface2, color: modalTab === t.key ? '#58a6ff' : C.text4, fontSize: 11, borderRadius: 20, padding: '1px 6px' }}>{t.count}</span>}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            {modalTab === 'emails' && <Btn onClick={copyAll} style={{ height: 30, padding: '0 10px', fontSize: 12 }}>{copied ? '✓ Copied' : 'Copy All'}</Btn>}
            {modalTab === 'emails' && <Btn onClick={downloadCsv} style={{ height: 30, padding: '0 10px', fontSize: 12, marginLeft: 6 }}>↓ CSV</Btn>}
            {modalTab === 'emails' && <Btn variant="success" onClick={() => setShowVerify(true)} style={{ height: 30, padding: '0 10px', fontSize: 12, marginLeft: 6 }}>✓ Verify</Btn>}
            <span style={{ fontSize: 11, color: daysLeft(result.expires_at) < 5 ? C.red : C.text4, marginLeft: 10 }}>{daysLeft(result.expires_at)}d left</span>
            <Btn variant="danger" onClick={() => onDelete(result.id)} style={{ height: 30, padding: '0 10px', fontSize: 12, marginLeft: 8 }}>Delete</Btn>
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {modalTab === 'emails' && (
              emails.length === 0
                ? <div style={{ padding: '48px 20px', textAlign: 'center', color: C.text3 }}>No emails in this result</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                      {['#', 'Email Address', 'Display Name', 'Count'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.text3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {emails.map((e, i) => (
                        <tr key={e.email} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 ? 'rgba(255,255,255,.015)' : 'transparent' }}>
                          <td style={{ padding: '9px 14px', color: C.text4, fontSize: 12, width: 44 }}>{i + 1}</td>
                          <td style={{ padding: '9px 14px', color: C.text, fontFamily: 'monospace', fontSize: 12 }}>{e.email}</td>
                          <td style={{ padding: '9px 14px', color: C.text3 }}>{e.name || '—'}</td>
                          <td style={{ padding: '9px 14px', color: C.text3 }}>{e.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
            )}
            {(modalTab === 'files' || modalTab === 'docs' || modalTab === 'images') && (() => {
              const rows = modalTab === 'files' ? otherFiles : modalTab === 'docs' ? docs : images;
              return rows.length === 0
                ? <div style={{ padding: '48px 20px', textAlign: 'center', color: C.text4, fontSize: 13 }}>No {modalTab} found in this extraction.</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                      {['#', 'Filename', 'Size', 'Email Subject', 'Download'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.text3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {rows.map((att, i) => (
                        <tr key={att.attachment_id || i} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 ? 'rgba(255,255,255,.015)' : 'transparent' }}>
                          <td style={{ padding: '9px 14px', color: C.text4, fontSize: 12, width: 40 }}>{i + 1}</td>
                          <td style={{ padding: '9px 14px', color: C.text, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ marginRight: 5 }}>{fileIcon(att.content_type, att.name)}</span>{att.name}</td>
                          <td style={{ padding: '9px 14px', color: C.text3, whiteSpace: 'nowrap' }}>{fmtSize(att.size)}</td>
                          <td style={{ padding: '9px 14px', color: C.text4, fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.subject || '—'}</td>
                          <td style={{ padding: '8px 14px' }}><Btn variant="accent" style={{ height: 28, padding: '0 10px', fontSize: 12 }} onClick={() => downloadAtt(att)}>↓ Download</Btn></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
            })()}
            {modalTab === 'keywords' && (
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {keywords.slice(0, 60).map(k => (
                    <span key={k.word} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 20, padding: '5px 12px', fontSize: 12, color: C.text2 }}>
                      {k.word} <span style={{ color: C.text4 }}>({k.count})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showVerify && (
        <VerifyModal emails={emails} sourceResultId={result.id}
          sourceAccountEmail={result.account_email} isSA={isSA}
          onClose={() => setShowVerify(false)}
          onSaved={() => { setShowVerify(false); onVerified && onVerified(); }} />
      )}
    </>
  );
}

// ── Verified emails detail modal ──────────────────────────────────────────────
function VerifDetailModal({ verif, isSA, onClose, onDelete }) {
  const results = verif.results || [];
  const downloadCsv = () => {
    const rows = [['Email', 'Status', 'Reason']];
    results.forEach(r => rows.push([r.email, r.status, r.reason || '']));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `verified_${verif.source_account_email}_${verif.created_at.split('T')[0]}.csv`;
    a.click();
  };
  const validOnly = () => {
    const valid = results.filter(r => r.status === 'valid').map(r => r.email).join('\n');
    navigator.clipboard.writeText(valid);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, width: '100%', maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,.5)' }}>
        <div style={{ padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border2}`, flexShrink: 0 }}>
          <div>
            <span style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>Verification — {verif.source_account_email}</span>
            {isSA && <span style={{ marginLeft: 8, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 11, color: C.text2 }}>by {verif.owner_username}</span>}
          </div>
          <Btn variant="flat" onClick={onClose} style={{ height: 32, padding: '0 10px', fontSize: 18, color: C.text3 }}>✕</Btn>
        </div>

        {/* Stats */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border2}`, display: 'flex', gap: 16, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: 'Valid', count: verif.valid, color: C.green },
            { label: 'Invalid', count: verif.invalid, color: C.red },
            { label: 'Uncertain', count: verif.unverifiable, color: C.yellow },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', minWidth: 80 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 11, color: C.text3 }}>{s.label}</div>
            </div>
          ))}
          <div style={{ flex: 1 }} />
          <Btn onClick={validOnly}>Copy Valid Only</Btn>
          <Btn onClick={downloadCsv}>↓ CSV</Btn>
          <Btn variant="danger" onClick={() => onDelete(verif.id)}>Delete</Btn>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                {['#', 'Email Address', 'Status', 'Detail'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.text3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={r.email + i} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 ? 'rgba(255,255,255,.015)' : 'transparent' }}>
                  <td style={{ padding: '9px 14px', color: C.text4, fontSize: 12, width: 44 }}>{i + 1}</td>
                  <td style={{ padding: '9px 14px', color: C.text, fontFamily: 'monospace', fontSize: 12 }}>{r.email}</td>
                  <td style={{ padding: '9px 14px' }}><Tag status={r.status} /></td>
                  <td style={{ padding: '9px 14px', color: C.text3, fontSize: 12 }}>{r.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Results page (Extracted Emails + Verified Emails tabs) ────────────────────
function ResultsPage({ isSA, onBack }) {
  const [tab, setTab] = useState('extracted');  // 'extracted' | 'verified'
  const [extracted, setExtracted] = useState([]);
  const [verified, setVerified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openExtract, setOpenExtract] = useState(null);
  const [openVerif, setOpenVerif] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [er, vr] = await Promise.all([
      fetch(`${API}/api/extractor/results`, { headers: hdr() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/api/extractor/verifications`, { headers: hdr() }).then(r => r.ok ? r.json() : []),
    ]);
    setExtracted(Array.isArray(er) ? er : []);
    setVerified(Array.isArray(vr) ? vr : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openExtractDetail = async row => {
    const r = await fetch(`${API}/api/extractor/results/${row.id}`, { headers: hdr() });
    if (r.ok) setOpenExtract(await r.json());
  };
  const openVerifDetail = async row => {
    const r = await fetch(`${API}/api/extractor/verifications/${row.id}`, { headers: hdr() });
    if (r.ok) setOpenVerif(await r.json());
  };
  const deleteExtract = async id => {
    if (!window.confirm('Delete this extraction result?')) return;
    await fetch(`${API}/api/extractor/results/${id}`, { method: 'DELETE', headers: hdr() });
    setExtracted(p => p.filter(r => r.id !== id));
    setOpenExtract(null);
  };
  const deleteVerif = async id => {
    if (!window.confirm('Delete this verification result?')) return;
    await fetch(`${API}/api/extractor/verifications/${id}`, { method: 'DELETE', headers: hdr() });
    setVerified(p => p.filter(v => v.id !== id));
    setOpenVerif(null);
  };
  const copyExtract = async row => {
    const r = await fetch(`${API}/api/extractor/results/${row.id}`, { headers: hdr() });
    if (!r.ok) return;
    const d = await r.json();
    navigator.clipboard.writeText((d.emails || []).map(e => e.email).join('\n'));
  };
  const dlCsv = async row => {
    const r = await fetch(`${API}/api/extractor/results/${row.id}`, { headers: hdr() });
    if (!r.ok) return;
    const d = await r.json();
    const rows = [['Email','Name','Count']]; (d.emails||[]).forEach(e => rows.push([e.email,e.name||'',e.count]));
    const csv = rows.map(r => r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = `emails_${row.account_email}_${row.created_at.split('T')[0]}.csv`; a.click();
  };

  const TH = ({ children }) => (
    <th style={{ padding: '10px 14px', textAlign: 'left', color: C.text3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: `2px solid ${C.border}`, background: C.surface }}>{children}</th>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sub-header */}
      <div style={{ padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: `1px solid ${C.border2}`, flexShrink: 0, height: 52 }}>
        <Btn variant="flat" onClick={onBack} style={{ padding: '0 8px', color: C.text3 }}>← Back</Btn>
        {/* Tab switcher */}
        <div style={{ display: 'flex', background: C.bg, borderRadius: 8, padding: 3, gap: 2, border: `1px solid ${C.border}` }}>
          {[
            { key: 'extracted', label: `Extracted Emails${extracted.length ? ` (${extracted.length})` : ''}` },
            { key: 'verified',  label: `Verified Emails${verified.length ? ` (${verified.length})` : ''}` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: tab === t.key ? C.surface : 'transparent',
              color: tab === t.key ? C.text : C.text3,
              fontWeight: tab === t.key ? 600 : 400,
              fontSize: 13, fontFamily: 'inherit',
              boxShadow: tab === t.key ? `0 1px 3px rgba(0,0,0,.3)` : 'none',
            }}>{t.label}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: C.text4 }}>Saved up to 30 days · Emails only</span>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 40px' }}>
        {loading && <div style={{ padding: '60px 24px', textAlign: 'center', color: C.text3, fontSize: 14 }}>Loading…</div>}

        {/* ── Extracted tab ── */}
        {!loading && tab === 'extracted' && (
          extracted.length === 0
            ? <EmptyState icon="📭" title="No extraction results yet" sub="Run an extraction to see email lists here." />
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>{['Date', 'Account', isSA && 'Admin', 'Emails', 'Expires', 'Actions'].filter(Boolean).map(h => <TH key={h}>{h}</TH>)}</tr></thead>
                <tbody>
                  {extracted.map((row, i) => (
                    <tr key={row.id} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 ? 'rgba(255,255,255,.015)' : 'transparent' }}>
                      <td style={{ padding: '11px 14px', color: C.text3, whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                      <td style={{ padding: '11px 14px', color: C.text, fontFamily: 'monospace', fontSize: 12 }}>{row.account_email}</td>
                      {isSA && <td style={{ padding: '11px 14px' }}><span style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 11, color: C.text2 }}>{row.owner_username}</span></td>}
                      <td style={{ padding: '11px 14px', fontWeight: 700, color: C.text }}>{row.total_emails.toLocaleString()}</td>
                      <td style={{ padding: '11px 14px' }}><span style={{ color: daysLeft(row.expires_at) < 5 ? C.red : C.text4, fontSize: 12 }}>{daysLeft(row.expires_at)}d left</span></td>
                      <td style={{ padding: '8px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Btn style={{ height: 32, padding: '0 12px', fontSize: 12 }} onClick={() => openExtractDetail(row)}>Open</Btn>
                          <Btn style={{ height: 32, padding: '0 12px', fontSize: 12 }} onClick={() => copyExtract(row)}>Copy</Btn>
                          <Btn style={{ height: 32, padding: '0 12px', fontSize: 12 }} onClick={() => dlCsv(row)}>↓ CSV</Btn>
                          <Btn variant="danger" style={{ height: 32, padding: '0 12px', fontSize: 12 }} onClick={() => deleteExtract(row.id)}>Delete</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        )}

        {/* ── Verified tab ── */}
        {!loading && tab === 'verified' && (
          verified.length === 0
            ? <EmptyState icon="✅" title="No verification results yet" sub="Open an extraction result and click 'Verify Emails', then save the results." />
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>{['Date', 'Account', isSA && 'Admin', 'Total', 'Valid', 'Invalid', 'Uncertain', 'Expires', 'Actions'].filter(Boolean).map(h => <TH key={h}>{h}</TH>)}</tr></thead>
                <tbody>
                  {verified.map((row, i) => (
                    <tr key={row.id} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 ? 'rgba(255,255,255,.015)' : 'transparent' }}>
                      <td style={{ padding: '11px 14px', color: C.text3, whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                      <td style={{ padding: '11px 14px', color: C.text, fontFamily: 'monospace', fontSize: 12 }}>{row.source_account_email}</td>
                      {isSA && <td style={{ padding: '11px 14px' }}><span style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 11, color: C.text2 }}>{row.owner_username}</span></td>}
                      <td style={{ padding: '11px 14px', color: C.text, fontWeight: 600 }}>{row.total.toLocaleString()}</td>
                      <td style={{ padding: '11px 14px', color: C.green, fontWeight: 600 }}>{row.valid}</td>
                      <td style={{ padding: '11px 14px', color: C.red, fontWeight: 600 }}>{row.invalid}</td>
                      <td style={{ padding: '11px 14px', color: C.yellow, fontWeight: 600 }}>{row.unverifiable}</td>
                      <td style={{ padding: '11px 14px' }}><span style={{ color: daysLeft(row.expires_at) < 5 ? C.red : C.text4, fontSize: 12 }}>{daysLeft(row.expires_at)}d left</span></td>
                      <td style={{ padding: '8px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Btn style={{ height: 32, padding: '0 12px', fontSize: 12 }} onClick={() => openVerifDetail(row)}>Open</Btn>
                          <Btn variant="danger" style={{ height: 32, padding: '0 12px', fontSize: 12 }} onClick={() => deleteVerif(row.id)}>Delete</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        )}
      </div>

      {openExtract && (
        <EmailDetailModal result={openExtract} isSA={isSA}
          onClose={() => setOpenExtract(null)}
          onDelete={id => { deleteExtract(id); setOpenExtract(null); }}
          onVerified={load} />
      )}
      {openVerif && (
        <VerifDetailModal verif={openVerif} isSA={isSA}
          onClose={() => setOpenVerif(null)}
          onDelete={id => { deleteVerif(id); setOpenVerif(null); }} />
      )}
    </div>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ color: C.text3, fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div style={{ color: C.text4, fontSize: 13 }}>{sub}</div>
    </div>
  );
}

// ── Right panel content (extraction progress & results) ───────────────────────
function ExtractionPanel({ job, resultDetail, selAcct, folderScope, selFolders,
                           isSA, onCancel, onNew, onResultVerified, onViewHistory }) {
  const scanned = job?.total_messages_scanned || 0;
  const found   = job?.emails_found || 0;
  const running = job?.status === 'running';
  const [resultCopied, setResultCopied] = useState(false);
  const [dataTab, setDataTab] = useState('emails');
  const [showVerify, setShowVerify] = useState(false);

  const copyAll = () => {
    const emails = (resultDetail?.emails || []).map(e => e.email).join('\n');
    navigator.clipboard.writeText(emails);
    setResultCopied(true); setTimeout(() => setResultCopied(false), 2000);
  };
  const downloadCsv = () => {
    const emails = resultDetail?.emails || [];
    const rows = [['Email','Name','Count']]; emails.forEach(e => rows.push([e.email,e.name||'',e.count]));
    const csv = rows.map(r => r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = `emails_${resultDetail?.account_email}_${(resultDetail?.created_at||'').split('T')[0]}.csv`;
    a.click();
  };

  if (!selAcct && !job) return (
    <EmptyState icon="📬" title="Select an Account"
      sub="Choose a connected email account from the left panel to get started." />
  );

  if (selAcct && !job) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <div style={{ maxWidth: 380, textAlign: 'center' }}>
        <div style={{ fontSize: 52, marginBottom: 20 }}>✅</div>
        <div style={{ color: C.text, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Ready to Extract</div>
        <div style={{ color: C.text3, fontSize: 13, marginBottom: 4 }}>{selAcct.provider === 'google' ? '🔴' : '🔵'} {selAcct.email}</div>
        <div style={{ color: C.text4, fontSize: 12, marginBottom: 28, lineHeight: 1.7 }}>
          {folderScope === 'all' ? 'All folders will be scanned.' : selFolders.length > 0 ? `${selFolders.length} folder(s) selected.` : 'All folders will be scanned.'}
          {' '}Up to 10,000 messages per run.
        </div>
        <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 24, textAlign: 'left' }}>
          <div style={{ fontSize: 11, color: C.text3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>How it works</div>
          <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.9 }}>
            <div>✉️ Scans matching emails → extracts <strong>contact addresses</strong></div>
            <div style={{ color: C.text4 }}>🔍 Keywords narrow which emails are scanned</div>
            <div style={{ color: C.text4 }}>📎 Attachment filter narrows by email type</div>
            <div style={{ color: C.text4 }}>💾 Results saved to server for 30 days</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (running) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 36, textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,.3)' }}>
          <div style={{ fontSize: 48, marginBottom: 16, display: 'inline-block', animation: 'spin 1.8s linear infinite' }}>⚙️</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 6 }}>Scanning Mailbox</div>
          <div style={{ fontSize: 12, color: C.text3, marginBottom: 28 }}>{job.account_email}</div>

          {/* Live counters */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 28, justifyContent: 'center' }}>
            <div style={{ flex: 1, background: C.bg, border: `1px solid ${C.border2}`, borderRadius: 10, padding: '16px 12px' }}>
              <div style={{ fontSize: 30, fontWeight: 700, color: C.accent, lineHeight: 1, letterSpacing: '-1px' }}>
                <AnimCounter target={scanned} running={running} />
              </div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>Messages Scanned</div>
            </div>
            <div style={{ flex: 1, background: C.bg, border: `1px solid ${C.border2}`, borderRadius: 10, padding: '16px 12px' }}>
              <div style={{ fontSize: 30, fontWeight: 700, color: C.green, lineHeight: 1, letterSpacing: '-1px' }}>
                <AnimCounter target={found} running={running} />
              </div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>Unique Emails Found</div>
            </div>
          </div>

          {/* Animated scanning bar */}
          <div style={{ height: 8, background: C.surface2, borderRadius: 4, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${C.accent}, #58a6ff, ${C.accent})`, backgroundSize: '200%', animation: 'scan-bar 1.6s ease-in-out infinite' }} />
          </div>

          <div style={{ fontSize: 12, color: C.text4, marginBottom: 24 }}>
            Scanning {folderScope === 'all' ? 'all folders' : `${selFolders?.length || 0} folder(s)`} · up to 10,000 messages
          </div>
          <Btn variant="danger" onClick={onCancel}>■ Stop Extraction</Btn>
        </div>
      </div>
    </div>
  );

  if (job?.status === 'failed') return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <div style={{ maxWidth: 400, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
        <div style={{ color: C.red, fontWeight: 700, fontSize: 16, marginBottom: 10 }}>Extraction Failed</div>
        <div style={{ color: C.text3, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>{job.error_message || 'An unexpected error occurred.'}</div>
        <Btn onClick={onNew}>Try Again</Btn>
      </div>
    </div>
  );

  if (job?.status === 'completed' && resultDetail) {
    const emails   = resultDetail.emails || [];
    const keywords = resultDetail.keywords || [];
    const showKwTab = keywords.length > 0;

    // Active filter tags to display
    const filterTags = [
      job.keywords_filter && { label: `🔍 "${job.keywords_filter}"`, color: C.accent },
      job.attachment_filter === 'any'    && { label: '📎 Has attachments', color: C.yellow },
      job.attachment_filter === 'images' && { label: '🖼️ Has images',      color: C.yellow },
      job.attachment_filter === 'docs'   && { label: '📄 Has documents',   color: C.yellow },
    ].filter(Boolean);

    return (
      <>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Summary bar */}
        <div style={{ flexShrink: 0, padding: '12px 24px', background: C.surface, borderBottom: `1px solid ${C.border2}`, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.green, lineHeight: 1 }}>{emails.length.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 3 }}>Unique Emails</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.accent, lineHeight: 1 }}>{job.total_messages_scanned.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 3 }}>Messages Matched</div>
            </div>
          </div>

          {/* Active filter chips */}
          {filterTags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: C.text4 }}>Filtered by:</span>
              {filterTags.map(t => (
                <span key={t.label} style={{ fontSize: 11, fontWeight: 600, color: t.color, background: `${t.color}18`, border: `1px solid ${t.color}40`, borderRadius: 20, padding: '3px 10px' }}>{t.label}</span>
              ))}
            </div>
          )}

          <div style={{ flex: 1 }} />
          <Btn variant="flat" onClick={onNew} style={{ color: C.text3 }}>New Extraction</Btn>
        </div>

        {/* Tab bar — only Emails and optionally Keywords */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '0 16px' }}>
          {[
            { key: 'emails',   label: 'Emails',   count: emails.length   },
            ...(showKwTab ? [{ key: 'keywords', label: 'Top Words', count: keywords.length }] : []),
          ].map(t => (
            <button key={t.key} onClick={() => setDataTab(t.key)} style={{
              height: 42, padding: '0 16px', border: 'none', cursor: 'pointer',
              background: 'transparent', fontFamily: 'inherit', fontSize: 13,
              color: dataTab === t.key ? C.text : C.text3,
              fontWeight: dataTab === t.key ? 600 : 400,
              borderBottom: dataTab === t.key ? `2px solid ${C.accent}` : '2px solid transparent',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {t.label}
              <span style={{ background: dataTab === t.key ? 'rgba(31,111,235,.2)' : C.surface2, color: dataTab === t.key ? '#58a6ff' : C.text4, fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '1px 7px' }}>{t.count.toLocaleString()}</span>
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {dataTab === 'emails' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn style={{ height: 30, padding: '0 12px', fontSize: 12 }} onClick={copyAll}>{resultCopied ? '✓ Copied!' : 'Copy All'}</Btn>
              <Btn style={{ height: 30, padding: '0 12px', fontSize: 12 }} onClick={downloadCsv}>↓ CSV</Btn>
              <Btn variant="success" style={{ height: 30, padding: '0 12px', fontSize: 12 }} onClick={() => setShowVerify(true)}>✓ Verify</Btn>
              {onViewHistory && <Btn variant="flat" style={{ height: 30, padding: '0 12px', fontSize: 12, color: C.accent }} onClick={onViewHistory}>History</Btn>}
            </div>
          )}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {dataTab === 'emails' && (
            emails.length === 0
              ? <EmptyState icon="📭" title="No contacts found" sub="No email addresses matched your search conditions." />
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                      {['#', 'Email Address', 'Display Name', 'Count'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: C.text3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((e, i) => (
                      <tr key={e.email} style={{ borderBottom: `1px solid ${C.border2}`, background: i % 2 ? 'rgba(255,255,255,.015)' : 'transparent' }}>
                        <td style={{ padding: '9px 16px', color: C.text4, fontSize: 12, width: 48 }}>{i + 1}</td>
                        <td style={{ padding: '9px 16px', color: C.text, fontFamily: 'monospace', fontSize: 12 }}>{e.email}</td>
                        <td style={{ padding: '9px 16px', color: C.text3 }}>{e.name || '—'}</td>
                        <td style={{ padding: '9px 16px', color: C.text3 }}>{e.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}

          {dataTab === 'keywords' && (
            <div style={{ padding: '24px 20px' }}>
              <div style={{ fontSize: 12, color: C.text3, marginBottom: 16 }}>
                Top {keywords.length} words from matching email subjects
                {job.keywords_filter && <span style={{ color: C.text4 }}> — relevance cloud for "{job.keywords_filter}"</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {keywords.map(k => {
                  const maxCount = keywords[0]?.count || 1;
                  const weight = k.count / maxCount;
                  const size = 11 + Math.round(weight * 10);
                  return (
                    <span key={k.word} style={{
                      background: `rgba(31,111,235,${0.08 + weight * 0.15})`,
                      border: `1px solid rgba(31,111,235,${0.2 + weight * 0.3})`,
                      borderRadius: 20, padding: '5px 12px',
                      fontSize: size, color: `rgba(88,166,255,${0.5 + weight * 0.5})`,
                      fontWeight: weight > 0.5 ? 600 : 400,
                    }}>
                      {k.word} <span style={{ fontSize: size - 2, opacity: 0.6 }}>({k.count})</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>

      {showVerify && (
        <VerifyModal
          emails={resultDetail?.emails || []}
          sourceResultId={resultDetail?.id}
          sourceAccountEmail={resultDetail?.account_email}
          isSA={isSA}
          onClose={() => setShowVerify(false)}
          onSaved={() => { setShowVerify(false); onResultVerified && onResultVerified(); }}
        />
      )}
      </>
    );
  }

  return null;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function EmailExtractor() {
  const { user } = useStore();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isSA = user?.role === 'superadmin';
  const isResults = location.pathname.endsWith('/results');

  const extractorUrl = isSA ? '/superadmin/email/extractor' : '/email/extractor';
  const resultsUrl   = extractorUrl + '/results';
  const backUrl      = isSA ? '/superadmin/email' : '/email';

  // Left panel state
  const [accounts,       setAccounts]       = useState([]);
  const [selAcct,        setSelAcct]        = useState(null);
  const [folders,        setFolders]        = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderScope,    setFolderScope]    = useState('all');
  const [selFolders,     setSelFolders]     = useState([]);
  // Mode: 'extract' = scan all emails for contacts | 'search' = find contacts by keyword/file
  const [mode, setMode] = useState('extract');
  // Search-mode conditions (only used when mode === 'search')
  const [kwInput,       setKwInput]       = useState('');
  const [attFilter,     setAttFilter]     = useState('none');   // none|any|images|docs
  const [extractKwCloud,setExtractKwCloud]= useState(false);

  // Job / result state
  const [job,          setJob]          = useState(null);
  const [polling,      setPolling]      = useState(false);
  const [resultDetail, setResultDetail] = useState(null);
  const [starting,     setStarting]     = useState(false);
  const pollRef = useRef(null);

  // Load accounts
  useEffect(() => {
    fetch(`${API}/api/emails/accounts`, { headers: hdr() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAccounts(data.filter(a => a.status === 'active')))
      .catch(() => {});
  }, [isSA]);

  // Load folders on account change
  useEffect(() => {
    if (!selAcct) { setFolders([]); return; }
    setLoadingFolders(true);
    setFolderScope('all'); setSelFolders([]);
    fetch(`${API}/api/extractor/folders/${selAcct.id}`, { headers: hdr() })
      .then(r => r.ok ? r.json() : [])
      .then(setFolders)
      .catch(() => setFolders([]))
      .finally(() => setLoadingFolders(false));
  }, [selAcct?.id]);

  // Poll job
  useEffect(() => {
    if (!job || !polling) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/extractor/jobs/${job.id}`, { headers: hdr() });
        if (!r.ok) return;
        const updated = await r.json();
        setJob(updated);
        if (updated.status !== 'running') {
          setPolling(false);
          clearInterval(pollRef.current);
          if (updated.status === 'completed') {
            // Fetch the result list to find this job's result
            const rr = await fetch(`${API}/api/extractor/results`, { headers: hdr() });
            if (rr.ok) {
              const all = await rr.json();
              const match = all.find(e => e.job_id === updated.id);
              if (match) {
                const rd = await fetch(`${API}/api/extractor/results/${match.id}`, { headers: hdr() });
                if (rd.ok) setResultDetail(await rd.json());
              }
            }
          }
        }
      } catch {}
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [job?.id, polling]);

  const startExtraction = async () => {
    if (!selAcct || starting) return;
    setStarting(true); setResultDetail(null);
    try {
      const isSearch = mode === 'search';
      const r = await fetch(`${API}/api/extractor/jobs`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({
          account_id: selAcct.id,
          keywords_filter: isSearch ? kwInput.trim() : '',
          attachment_filter: isSearch ? attFilter : 'none',
          extract_keywords: isSearch ? extractKwCloud : false,
          folder_scope: folderScope === 'all' ? 'all' : selFolders.join(',') || 'all',
        }),
      });
      if (r.ok) { const j = await r.json(); setJob(j); setPolling(true); }
    } finally { setStarting(false); }
  };

  const cancelJob = () => { setPolling(false); clearInterval(pollRef.current); setJob(null); setResultDetail(null); };
  const newExtraction = () => { setJob(null); setResultDetail(null); };
  const toggleFolder = id => setSelFolders(p => p.includes(id) ? p.filter(f => f !== id) : [...p, id]);

  const SectionLabel = ({ children }) => (
    <div style={{ padding: '10px 14px 6px', fontSize: 10, color: C.text3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', background: C.bg, borderBottom: `1px solid ${C.border2}` }}>
      {children}
    </div>
  );

  if (isResults) return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: C.bg, color: C.text, fontFamily: 'system-ui,sans-serif', overflow: 'hidden' }}>
      <div style={{ padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', gap: 12, background: C.surface, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <Btn variant="flat" onClick={() => navigate(extractorUrl)} style={{ color: C.text3 }}>← Extractor</Btn>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Extracted & Verified Emails</span>
      </div>
      <ResultsPage isSA={isSA} onBack={() => navigate(extractorUrl)} />
      <Styles />
    </div>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: C.bg, color: C.text, fontFamily: 'system-ui,sans-serif', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn variant="flat" onClick={() => navigate(backUrl)} style={{ color: C.text3 }}>← Email</Btn>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Extractor</span>
          <span style={{ fontSize: 11, color: C.text3, background: C.surface2, border: `1px solid ${C.border}`, padding: '3px 10px', borderRadius: 20 }}>Email Contact Extractor</span>
        </div>
        <Btn variant="accent" onClick={() => navigate(resultsUrl)}>Extracted Emails →</Btn>
      </div>

      {/* Body — split pane */}
      <div style={{ display: 'grid', gridTemplateColumns: '270px 1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* ── Left panel ── */}
        <div style={{ borderRight: `1px solid ${C.border}`, overflowY: 'auto', background: C.bg, display: 'flex', flexDirection: 'column' }}>

          {/* Mode toggle — two distinct tools */}
          <div style={{ padding: '10px 12px', background: C.surface, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', background: C.bg, borderRadius: 8, padding: 3, gap: 2, border: `1px solid ${C.border}` }}>
              {[
                { key: 'extract', label: '✉️ Extract Contacts' },
                { key: 'search',  label: '🔍 Find by Keyword/File' },
              ].map(m => (
                <button key={m.key} onClick={() => { setMode(m.key); setJob(null); setResultDetail(null); }} style={{
                  flex: 1, height: 34, borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 11, fontWeight: mode === m.key ? 700 : 400,
                  background: mode === m.key ? C.surface : 'transparent',
                  color: mode === m.key ? C.text : C.text3,
                  boxShadow: mode === m.key ? '0 1px 3px rgba(0,0,0,.3)' : 'none',
                }}>{m.label}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: C.text4, marginTop: 6, lineHeight: 1.5 }}>
              {mode === 'extract'
                ? 'Extracts all contact email addresses from the selected account.'
                : 'Finds contacts only from emails matching your keywords or file type.'}
            </div>
          </div>

          {/* Account selector */}
          <SectionLabel>Select Account</SectionLabel>
          {accounts.length === 0
            ? <div style={{ padding: '16px 14px', fontSize: 13, color: C.text4 }}>No active connected accounts</div>
            : accounts.map(acct => (
              <button key={acct.id} onClick={() => setSelAcct(acct)}
                style={{ width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none',
                  borderBottom: `1px solid ${C.border2}`, cursor: 'pointer', background: 'transparent',
                  color: C.text, borderLeft: selAcct?.id === acct.id ? `3px solid ${C.green}` : '3px solid transparent',
                  fontFamily: 'inherit',
                  backgroundColor: selAcct?.id === acct.id ? 'rgba(46,160,67,.08)' : 'transparent' }}>
                <div style={{ fontSize: 13, fontWeight: selAcct?.id === acct.id ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {acct.provider === 'google' ? '🔴' : '🔵'} {acct.email}
                </div>
                {isSA && acct.owner_username && <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>by {acct.owner_username}</div>}
              </button>
            ))
          }

          {/* Search-mode conditions — only shown in Find mode */}
          {mode === 'search' && (
            <>
              <SectionLabel>Search Conditions</SectionLabel>
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Keywords */}
                <div>
                  <div style={{ fontSize: 11, color: C.text3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 7 }}>Keywords</div>
                  <input
                    value={kwInput}
                    onChange={e => setKwInput(e.target.value)}
                    placeholder="invoice, payment, proposal…"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: C.surface2, border: `1px solid ${kwInput ? C.accent : C.border}`,
                      borderRadius: 6, padding: '8px 10px', fontSize: 12, color: C.text,
                      fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <div style={{ fontSize: 11, color: C.text4, marginTop: 4 }}>Only scan emails containing these words</div>
                </div>

                {/* Attachment filter */}
                <div>
                  <div style={{ fontSize: 11, color: C.text3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Attachment Type</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {[
                      { val: 'none',   label: 'Any email',               sub: 'No attachment filter' },
                      { val: 'any',    label: 'Has any attachment',       sub: 'Files, images, or docs' },
                      { val: 'images', label: 'Has image attachments',    sub: 'JPG, PNG, GIF, etc.' },
                      { val: 'docs',   label: 'Has document attachments', sub: 'PDF, Word, Excel, etc.' },
                    ].map(opt => (
                      <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer' }}>
                        <input type="radio" name="attFilter" value={opt.val}
                          checked={attFilter === opt.val}
                          onChange={() => setAttFilter(opt.val)}
                          style={{ accentColor: C.green, marginTop: 3, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 12, color: attFilter === opt.val ? C.text : C.text2, fontWeight: attFilter === opt.val ? 600 : 400 }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: C.text4 }}>{opt.sub}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Word cloud option */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', borderTop: `1px solid ${C.border2}`, paddingTop: 10 }}>
                  <input type="checkbox" checked={extractKwCloud}
                    onChange={e => setExtractKwCloud(e.target.checked)}
                    style={{ accentColor: C.green, width: 14, height: 14, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, color: C.text }}>Show topic word cloud</div>
                    <div style={{ fontSize: 11, color: C.text4 }}>Top words from matching subjects</div>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* Folder scope — shown in both modes */}
          {selAcct && (
            <>
              <SectionLabel>Folder Scope</SectionLabel>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {['all', 'specific'].map(v => (
                    <button key={v} onClick={() => setFolderScope(v)} style={{
                      flex: 1, height: 34, borderRadius: 6, border: `1px solid ${folderScope === v ? C.green : C.border}`,
                      background: folderScope === v ? 'rgba(46,160,67,.12)' : C.surface2,
                      color: folderScope === v ? C.green : C.text2,
                      fontWeight: folderScope === v ? 600 : 400, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                    }}>{v === 'all' ? 'All Folders' : 'Specific'}</button>
                  ))}
                </div>
                {folderScope === 'specific' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                    {loadingFolders
                      ? <div style={{ fontSize: 12, color: C.text3, padding: '8px 0' }}>Loading…</div>
                      : folders.length === 0
                        ? <div style={{ fontSize: 12, color: C.text4 }}>No folders found</div>
                        : folders.map(f => (
                          <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '2px 0' }}>
                            <input type="checkbox" checked={selFolders.includes(f.id)} onChange={() => toggleFolder(f.id)}
                              style={{ accentColor: C.green, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: C.text2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                            {f.count > 0 && <span style={{ fontSize: 11, color: C.text4, flexShrink: 0 }}>{f.count.toLocaleString()}</span>}
                          </label>
                        ))
                    }
                  </div>
                )}
              </div>
            </>
          )}

          {/* Action button */}
          <div style={{ marginTop: 'auto', padding: '14px', borderTop: `1px solid ${C.border2}` }}>
            {!job || job.status !== 'running'
              ? <Btn variant="primary" onClick={startExtraction}
                  disabled={!selAcct || starting || job?.status === 'running'}
                  style={{ width: '100%', height: 40, fontSize: 14 }}>
                  {starting ? '⏳ Starting…' : mode === 'extract' ? '▶  Extract Contacts' : '🔍  Find Contacts'}
                </Btn>
              : <Btn variant="danger" onClick={cancelJob} style={{ width: '100%', height: 40, fontSize: 14 }}>
                  ■  Stop
                </Btn>
            }
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ background: C.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ExtractionPanel
            job={job} resultDetail={resultDetail} selAcct={selAcct}
            folderScope={folderScope} selFolders={selFolders} isSA={isSA}
            onCancel={cancelJob} onNew={newExtraction} onResultVerified={() => {}}
            onViewHistory={() => navigate(resultsUrl)} />
        </div>
      </div>

      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes scan-bar {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `}</style>
  );
}
